import PropTypes from 'prop-types';
import React, { forwardRef, useMemo } from 'react';
import AgGrid from 'components/molecules/agGrid';
import { useMovementsTableRows } from './hooks/useMovementsTableRows';
import { MovementContactRenderer } from './movementsRenderers/movementContactRenderer';
import { MovementCompanyRenderer } from './movementsRenderers/movementCompanyRenderer';
import { MovementJobTitleRenderer } from './movementsRenderers/movementJobTitleRenderer';
import { MovementTypeBadge } from './movementsRenderers/movementTypeBadge';
import { MovementDateRenderer } from './movementsRenderers/movementDateRenderer';

export const MovementsTableView = forwardRef(
  (
    {
      data,
      activeNetwork,
      columns,
      setColumns,
      onLoadMore,
      hasMore,
      isLoadingMore,
      onSort,
      initialSortBy,
      sx,
      showSkeleton,
    },
    ref,
  ) => {
    const rows = useMovementsTableRows(data, showSkeleton);

    const sortableColumns = useMemo(
      () => [
        {
          accessor: 'date',
          Header: 'Date',
          disableSortBy: false,
        },
      ],
      [],
    );

    const defaultInitialSortBy = useMemo(
      () => ({
        id: 'date',
        desc: true,
      }),
      [],
    );

    return (
      <AgGrid
        ref={ref}
        disablePinnedOnMobile
        initialSortBy={initialSortBy || defaultInitialSortBy}
        sortColumns={sortableColumns}
        enableLeftTableBorder={false}
        useExternalStoredColumns
        storedColumns={columns}
        setStoredColumns={setColumns}
        itemName="movement"
        isInitialized
        canSort
        onSort={([item]) => {
          if (onSort) {
            onSort({
              sortBy: item.id,
              sortDirection: item.desc ? 'desc' : 'asc',
            });
          }
        }}
        canSelect={false}
        hasMore={hasMore}
        loadMore={onLoadMore}
        items={rows}
        totalItems={rows.length}
        columnDefinition={columns}
        isLoadingMore={isLoadingMore}
        onColumnAction={() => {}}
        persistColumnKey={`${activeNetwork?.id}-movements-columns-v1`}
        showActions={false}
        cellStyle={{
          padding: '0px',
        }}
        components={{
          movementContactRenderer: MovementContactRenderer,
          movementCompanyRenderer: MovementCompanyRenderer,
          movementJobTitleRenderer: MovementJobTitleRenderer,
          movementTypeBadge: MovementTypeBadge,
          movementDateRenderer: MovementDateRenderer,
        }}
        rowActions={{
          height: 330,
          items: [],
        }}
        sx={{
          height: 'calc(100vh - 200px)',
          '.ag-root-wrapper': {
            border: 'none',
          },
          '.ag-header-cell[col-id="contact"]': {
            paddingLeft: '16px',
          },
          '@media screen and (max-width: 992px)': {
            '[col-id="selected"], .ag-pinned-left-header ': {
              borderRight: 'none !important',
            },
          },
          ...sx,
        }}
        gridOptions={{
          getRowClass: () => {},
          rowHeight: 48,
          suppressRowClickSelection: true,
        }}
      />
    );
  },
);

MovementsTableView.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeNetwork: PropTypes.object,
  columns: PropTypes.array,
  setColumns: PropTypes.func,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  onSort: PropTypes.func,
  initialSortBy: PropTypes.shape({
    id: PropTypes.string,
    desc: PropTypes.bool,
  }),
  sx: PropTypes.object,
  showSkeleton: PropTypes.bool,
};

MovementsTableView.defaultProps = {
  activeNetwork: {},
  columns: null,
  setColumns: () => {},
  onLoadMore: () => {},
  hasMore: false,
  isLoadingMore: false,
  onSort: null,
  initialSortBy: null,
  sx: {},
  showSkeleton: false,
};
